import { format, isValid, parseISO } from 'date-fns'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'

const TRACKING_WINDOW_TIME_24_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const TRACKING_WINDOW_TIME_12_PATTERN = /^(1[0-2]|[1-9]):([0-5]\d) (AM|PM)$/i

const getTrackingWindowTimeParts = (time: string) => {
  const twentyFourHourMatch = time.match(TRACKING_WINDOW_TIME_24_PATTERN)

  if (twentyFourHourMatch) {
    const [, hours, minutes] = twentyFourHourMatch

    return {
      hours: Number.parseInt(hours, 10),
      minutes: Number.parseInt(minutes, 10)
    }
  }

  const twelveHourMatch = time.match(TRACKING_WINDOW_TIME_12_PATTERN)

  if (!twelveHourMatch) {
    return null
  }

  const [, hours, minutes, meridiem] = twelveHourMatch
  const parsedHours = Number.parseInt(hours, 10)
  const normalizedMeridiem = meridiem.toUpperCase()
  const normalizedHours =
    normalizedMeridiem === 'PM' ? (parsedHours === 12 ? 12 : parsedHours + 12) : parsedHours === 12 ? 0 : parsedHours

  return {
    hours: normalizedHours,
    minutes: Number.parseInt(minutes, 10)
  }
}

export const normalizeTrackingWindowTime = (time: string): string => {
  const timeParts = getTrackingWindowTimeParts(time)

  if (!timeParts) {
    return ''
  }

  return `${timeParts.hours.toString().padStart(2, '0')}:${timeParts.minutes.toString().padStart(2, '0')}`
}

export const combineDateTime = (date: Date, time: string, timezone: string): string => {
  const timeParts = getTrackingWindowTimeParts(time)

  if (!isValid(date) || !timeParts) {
    return ''
  }

  const combined = new Date(date)
  combined.setHours(timeParts.hours, timeParts.minutes, 0, 0)

  return isValid(combined) ? formatInTimeZone(combined, timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : ''
}

export const isTrackingWindowComplete = (
  startDate: Date | undefined,
  startTime: string,
  endDate: Date | undefined,
  endTime: string
): boolean => {
  return Boolean(
    startDate &&
      endDate &&
      isValid(startDate) &&
      isValid(endDate) &&
      getTrackingWindowTimeParts(startTime) &&
      getTrackingWindowTimeParts(endTime)
  )
}

export const parseTrackingWindowDate = (value: string | null, timezone: string): Date | undefined => {
  if (!value) {
    return undefined
  }

  const parsedDate = parseISO(value)

  if (!isValid(parsedDate)) {
    return undefined
  }

  const zonedDate = toZonedTime(parsedDate, timezone)

  return isValid(zonedDate) ? zonedDate : undefined
}

export const getTrackingWindowSummary = ({
  endDate,
  endTime,
  startDate,
  startTime
}: {
  endDate: Date | undefined
  endTime: string
  startDate: Date | undefined
  startTime: string
}) => {
  const buildSummaryDate = (date: Date | undefined, time: string) => {
    const timeParts = getTrackingWindowTimeParts(time)

    if (!date || !isValid(date) || !timeParts) {
      return null
    }

    const summaryDate = new Date(date)
    summaryDate.setHours(timeParts.hours, timeParts.minutes, 0, 0)

    return isValid(summaryDate) ? format(summaryDate, 'MMM d, h:mm a') : null
  }

  const fromValue = buildSummaryDate(startDate, startTime)
  const toValue = buildSummaryDate(endDate, endTime)

  if (fromValue && toValue) return `${fromValue} - ${toValue}`
  if (fromValue) return `From ${fromValue}`
  if (toValue) return `Until ${toValue}`
  return null
}
